import { LayerData, meshPickTargetsAtom, selectedFragmentElementAtom } from '@/atoms';
import type { FragmentPropertyNode } from '@/atoms/fragmentSelection.atom';
import { clipBoxStateAtom, fragmentPickFnAtom, viewerEvents } from '@/atoms/viewer.atom';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { autoClassColor } from '@/lib/fragmentColors';
import { useFrame, useThree } from '@react-three/fiber';
import * as FRAGS from '@thatopen/fragments';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';

/**
 * IFC2 – Displays pre-converted ThatOpen Fragment (.frag) files.
 *
 * Layers whose metadata.type === 'FRAGMENT' are expected to have `root`
 * pointing at the .frag file on the resources endpoint.
 *
 * The FragmentsModels engine is initialised once (module-level singleton)
 * and each layer gets its own model loaded into the Three.js scene.
 */

// ---------------------------------------------------------------------------
// Module-level singleton – shared across all IFC2 instances
// ---------------------------------------------------------------------------
let fragmentsInstance: FRAGS.FragmentsModels | null = null;
let fragmentsInitPromise: Promise<FRAGS.FragmentsModels> | null = null;

/** modelId → THREE.Group used to mount each loaded model. Lets external
 *  callers (jumpToFragment, future tools) transform model-local coordinates
 *  into world coordinates without poking at IFC2's internals. */
const modelGroups = new Map<string, THREE.Group>();

/**
 * Frame the camera on a fragment item by emitting `jumpToTarget` with the
 * world-space center of its bounding box. Mirrors the pattern used by the
 * attribute-table and layer "jump to center" actions.
 */
export async function jumpToFragment(modelId: string, localId: number): Promise<void> {
  const fragments = await getFragments();
  const model = fragments.models.list.get(modelId);
  if (!model) return;
  let boxes: THREE.Box3[] = [];
  try {
    boxes = await model.getBoxes([localId]);
  } catch {
    return;
  }
  if (!boxes.length) return;
  const center = boxes[0].getCenter(new THREE.Vector3());
  const group = modelGroups.get(modelId);
  if (group) group.localToWorld(center);
  viewerEvents.emit('jumpToTarget', { x: center.x, y: center.y, z: center.z });
}

/** Returns the singleton synchronously, or null if not yet initialized. */
export function getFragmentsSync(): FRAGS.FragmentsModels | null {
  return fragmentsInstance;
}

/** Expose the singleton so other modules (e.g. FragmentProperties) can access it. */
export async function getFragments(): Promise<FRAGS.FragmentsModels> {
  if (fragmentsInstance) return fragmentsInstance;
  if (fragmentsInitPromise) return fragmentsInitPromise;

  fragmentsInitPromise = (async () => {
    // Load the worker from the public directory
    // (copied from node_modules/@thatopen/fragments/dist/Worker/worker.mjs)
    const workerUrl = '/fragments.mjs';

    const fragments = new FRAGS.FragmentsModels(workerUrl);

    // Disable auto-coordination — we handle georeferenced positioning ourselves
    fragments.settings.autoCoordinate = false;

    // Reduce z-fighting on materials
    fragments.models.materials.list.onItemSet.add(({ value: material }) => {
      if (!('isLodMaterial' in material && material.isLodMaterial)) {
        material.polygonOffset = true;
        material.polygonOffsetUnits = 1;
        material.polygonOffsetFactor = Math.random();
      }
    });

    fragmentsInstance = fragments;
    return fragments;
  })();

  return fragmentsInitPromise;
}

// ---------------------------------------------------------------------------
// Helpers – convert ItemData tree to our flat property nodes
// ---------------------------------------------------------------------------

/**
 * Default config for `model.getItemsData` — fetches default attributes plus
 * property-bearing relations recursively. We explicitly list the relations
 * that carry useful property data (property sets, type info, spatial context)
 * and enable `relations: true` on them so nested custom entities (e.g.
 * CadmaticObject inside a PropertySet) also expose their attributes.
 *
 * We intentionally do NOT set `relationsDefault: { relations: true }` because
 * that causes the library to traverse the entire IFC graph (thousands of
 * interconnected elements), freezing the browser.
 */
const ITEMS_DATA_CONFIG: Partial<FRAGS.ItemsDataConfig> = {
  attributesDefault: true,
  relationsDefault: { attributes: false, relations: false },
  relations: {
    IsDefinedBy: { attributes: true, relations: true },
    IsTypedBy: { attributes: true, relations: true },
    ContainedInStructure: { attributes: true, relations: false },
    HasProperties: { attributes: true, relations: true },
  },
};

/**
 * Recursively convert the ItemData tree returned by `element.getData()`
 * into our serialisable FragmentPropertyNode tree.
 *
 * Because `relationsDefault: { relations: true }` can produce circular
 * references (element A → B → A), we track visited localIds and cap
 * recursion depth to avoid blowing the stack.
 */
const MAX_PROPERTY_DEPTH = 8;

function itemDataToNodes(
  data: FRAGS.ItemData,
  visited: Set<number> = new Set(),
  depth: number = 0,
): FragmentPropertyNode[] {
  if (depth > MAX_PROPERTY_DEPTH) return [];

  const nodes: FragmentPropertyNode[] = [];
  const localId = (data._localId as FRAGS.ItemAttribute)?.value ?? 0;

  // Cycle guard — skip if we've already traversed this item
  if (localId && visited.has(localId)) return [];
  if (localId) visited.add(localId);

  for (const name in data) {
    if (name.startsWith('_')) continue;
    const current = data[name];

    if (Array.isArray(current)) {
      // Relation – recurse into children
      const childNodes: FragmentPropertyNode[] = [];
      for (const child of current) {
        const childLocalId = (child._localId as FRAGS.ItemAttribute)?.value ?? 0;
        // Skip circular references at the child level too
        if (childLocalId && visited.has(childLocalId)) continue;
        childNodes.push({
          name: String(childLocalId),
          localId: childLocalId,
          type: 'related',
          children: itemDataToNodes(child, new Set(visited), depth + 1),
        });
      }
      if (childNodes.length > 0) {
        nodes.push({
          name,
          localId,
          type: 'relation',
          children: childNodes,
        });
      }
    } else {
      // Attribute
      const attr = current as FRAGS.ItemAttribute;
      if (attr.value === undefined || attr.value === null) continue;
      nodes.push({
        name,
        value: attr.value,
        localId,
        type: 'attribute',
      });
    }
  }
  return nodes;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const IFC2 = ({ layers }: { layers: LayerData[] }) => {
  const { camera, gl, scene } = useThree();
  const { projectId } = useParams();
  const [loadedIds, setLoadedIds] = useState<Set<string>>(new Set());
  const groupRef = useRef<THREE.Group>(null);
  const setPickTargets = useSetAtom(meshPickTargetsAtom);
  const setFragmentPickFn = useSetAtom(fragmentPickFnAtom);
  const [selectedFragment, setSelectedFragment] = useAtom(selectedFragmentElementAtom);
  const layerSettings = useLayerSettingsMany(layers.map((l) => l.id));
  const clipBoxState = useAtomValue(clipBoxStateAtom(projectId!));

  // Persistent clipping planes array (reused to avoid GC churn)
  const clipPlanesRef = useRef<THREE.Plane[]>([]);

  // Highlight state: the mesh group + the Element (needed for disposeMeshes)
  const highlightRef = useRef<THREE.Group | null>(null);
  const elementRef = useRef<FRAGS.Element | null>(null);

  // Map modelId → layerId for reverse lookup on raycast hit
  const modelToLayerRef = useRef<Map<string, string>>(new Map());
  // Map modelId → world position so we can place the highlight in the same spot
  const modelWorldPosRef = useRef<Map<string, THREE.Vector3>>(new Map());

  /** Remove the current highlight mesh from the scene and dispose it properly. */
  const clearHighlight = useCallback(() => {
    if (highlightRef.current) {
      highlightRef.current.parent?.remove(highlightRef.current);
      if (elementRef.current) {
        try {
          elementRef.current.disposeMeshes(highlightRef.current);
        } catch {
          // fallback: just remove from scene
        }
      }
      highlightRef.current = null;
      elementRef.current = null;
    }
  }, []);

  // Track which (modelId,localId) we've already fetched + highlighted so the
  // effect below doesn't re-run when it writes its own properties back.
  const appliedSelectionRef = useRef<string | null>(null);

  // Respond to ANY selection change (raycast, tree click, programmatic) by
  // clearing the old highlight and — if there is a new selection — fetching
  // properties and building the gold highlight mesh.
  useEffect(() => {
    if (!selectedFragment) {
      clearHighlight();
      appliedSelectionRef.current = null;
      return;
    }
    const key = `${selectedFragment.modelId}#${selectedFragment.localId}`;
    if (appliedSelectionRef.current === key) return;
    appliedSelectionRef.current = key;

    let cancelled = false;
    (async () => {
      clearHighlight();
      try {
        const fragments = await getFragments();
        const model = fragments.models.list.get(selectedFragment.modelId);
        if (!model || cancelled) return;

        // Properties via getItemsData — works for ALL items including
        // relationship/aggregation containers (IfcRelAggregates, etc.) that
        // editor.getElements would skip.
        const datas = await model.getItemsData([selectedFragment.localId], ITEMS_DATA_CONFIG);
        if (cancelled) return;
        const data = datas?.[0];
        const properties = data ? itemDataToNodes(data) : [];
        const guid = data ? (data._guid as FRAGS.ItemAttribute | undefined)?.value : undefined;
        if (guid !== undefined && guid !== null) {
          properties.unshift({
            name: 'GUID',
            value: guid,
            localId: selectedFragment.localId,
            type: 'attribute',
          });
        }

        // Highlight via Element — best-effort; relationship items have no
        // geometry so this is allowed to no-op.
        try {
          const [element] = await fragments.editor.getElements(selectedFragment.modelId, [selectedFragment.localId]);
          if (element && !cancelled) {
            const mesh = await element.getMeshes();
            if (!cancelled) {
              mesh.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                  const mat = child.material as THREE.MeshLambertMaterial;
                  mat.depthTest = false;
                  mat.color.set('#FFD700');
                  mat.transparent = true;
                  mat.opacity = 0.6;
                }
              });
              const worldPos = modelWorldPosRef.current.get(selectedFragment.modelId) ?? new THREE.Vector3();
              const container = new THREE.Group();
              container.position.copy(worldPos);
              container.rotation.set(Math.PI / 2, 0, 0);
              container.add(mesh);
              scene.add(container);
              highlightRef.current = container;
              elementRef.current = element;
            }
          }
        } catch {
          // highlight is best-effort
        }

        if (cancelled) return;
        setSelectedFragment((prev) =>
          prev && prev.modelId === selectedFragment.modelId && prev.localId === selectedFragment.localId
            ? { ...prev, properties, loading: false }
            : prev,
        );
      } catch (err) {
        console.error('[IFC2] Failed to get element data:', err);
        if (!cancelled) setSelectedFragment(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFragment, clearHighlight, scene, setSelectedFragment]);

  // Escape key deselects
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedFragment) {
        setSelectedFragment(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedFragment, setSelectedFragment]);

  // Double-click handler: raycast all loaded fragment models, pick closest,
  // then seed the selection atom in loading state — the effect above does
  // the rest (fetch + highlight) so tree clicks share the same code path.
  const handleDoubleClick = useCallback(
    async (event: MouseEvent) => {
      const fragments = await getFragments();
      const mouse = new THREE.Vector2(event.clientX, event.clientY);

      let bestResult: FRAGS.RaycastResult | null = null;
      for (const [, model] of fragments.models.list) {
        try {
          const result = await model.raycast({ camera: camera as THREE.PerspectiveCamera, mouse, dom: gl.domElement });
          if (result && (!bestResult || result.distance < bestResult.distance)) {
            bestResult = result;
          }
        } catch {
          // model may not support raycasting yet
        }
      }
      if (!bestResult) return;

      const modelId = bestResult.fragments.modelId;
      const layerId = modelToLayerRef.current.get(modelId) ?? '';
      setSelectedFragment({
        localId: bestResult.localId,
        modelId,
        layerId,
        properties: [],
        loading: true,
      });
    },
    [camera, gl.domElement, setSelectedFragment],
  );

  // Stable key — re-runs the load effect when layers change or wireframe
  // is toggled. Changing wireframe triggers a full dispose+reload cycle
  // because ThatOpen's LOD geometries are incompatible with Three.js's
  // built-in wireframe pipeline when toggled mid-session.
  const layersKey = useMemo(
    () => layers.map((l) => `${l.id}:${l.root}:${layerSettings[l.id]?.modelIsWireframe}`).join(','),
    [layers, layerSettings],
  );

  // Apply opacity via ThatOpen's setOpacity API when settings change.
  // This handles both LOD and standard materials correctly through the
  // streaming system, including newly streamed tiles.
  const isHighlight =
    clipBoxState.enabled && clipBoxState.center !== null && clipBoxState.clipMode === 'HIGHLIGHT_INSIDE';
  const opacityKey = useMemo(
    () => layers.map((l) => `${l.id}:${layerSettings[l.id]?.modelOpacity}:${isHighlight}`).join(','),
    [layers, layerSettings, isHighlight],
  );
  useEffect(() => {
    if (!fragmentsInstance) return;
    const dimFactor = isHighlight ? 0.25 : 1.0;

    for (const layer of layers) {
      const modelId = `frag-${layer.id}`;
      const model = fragmentsInstance.models.list.get(modelId);
      if (!model) continue;
      const settings = layerSettings[layer.id];
      if (!settings) continue;

      const baseOpacity = settings.modelOpacity ?? 1.0;
      const opacity = baseOpacity * dimFactor;
      model.setOpacity(undefined, opacity);
    }
  }, [opacityKey, layers, layerSettings, isHighlight]);

  // --- Color-by-class (per-fragment-layer setting) ---
  // Reads modelColorBy + modelClassColors from the GraphQL-backed layer
  // settings; re-runs on change or when a model finishes loading.
  const colorByKey = useMemo(
    () =>
      layers
        .map((l) => {
          const s = layerSettings[l.id];
          return `${l.id}:${s?.modelColorBy ?? 'DEFAULT'}:${JSON.stringify(s?.modelClassColors ?? [])}:${loadedIds.has(`frag-${l.id}`) ? '1' : '0'}`;
        })
        .join('|'),
    [layers, layerSettings, loadedIds],
  );
  useEffect(() => {
    if (!fragmentsInstance) return;
    let cancelled = false;
    (async () => {
      for (const layer of layers) {
        const modelId = `frag-${layer.id}`;
        const model = fragmentsInstance!.models.list.get(modelId);
        if (!model) continue;
        const settings = layerSettings[layer.id];
        const mode = settings?.modelColorBy ?? 'DEFAULT';
        const classColors = settings?.modelClassColors ?? [];
        try {
          if (mode === 'DEFAULT') {
            await model.resetColor(undefined);
          } else if (mode === 'CLASS') {
            const colorByClass = new Map<string, string>();
            for (const c of classColors) colorByClass.set(c.class, c.color);
            // Single regex matches every category — result keys by actual class name
            const itemsByCat = await model.getItemsOfCategories([/.*/]);
            if (cancelled) return;
            for (const [cat, ids] of Object.entries(itemsByCat)) {
              if (cancelled) return;
              if (!ids || ids.length === 0) continue;
              const hex = colorByClass.get(cat) ?? autoClassColor(cat);
              await model.setColor(ids, new THREE.Color(hex));
            }
          }
        } catch (err) {
          console.warn(`[IFC2] color-by failed for ${modelId}:`, err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [colorByKey, layerSettings, layers]);

  // Register the group as a pick target for measuring tools
  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    setPickTargets((prev) => [...prev, group]);
    return () => setPickTargets((prev) => prev.filter((t) => t !== group));
  }, [setPickTargets]);

  // Register fragment pick function for measuring tools
  useEffect(() => {
    const pickFn = async (cam: THREE.Camera, mouse: THREE.Vector2, dom: HTMLElement) => {
      if (!fragmentsInstance || fragmentsInstance.models.list.size === 0) return null;
      let bestPoint: THREE.Vector3 | null = null;
      let bestDist = Infinity;
      for (const [, model] of fragmentsInstance.models.list) {
        try {
          const result = await model.raycast({
            camera: cam as THREE.PerspectiveCamera,
            mouse,
            dom,
          });
          if (result && result.distance < bestDist) {
            bestDist = result.distance;
            bestPoint = result.point.clone();
          }
        } catch {
          // model may not support raycasting
        }
      }
      return bestPoint;
    };
    setFragmentPickFn(() => pickFn);
    return () => setFragmentPickFn(null);
  }, [setFragmentPickFn]);

  // Double-click listener for element picking
  useEffect(() => {
    const canvas = gl.domElement;
    canvas.addEventListener('dblclick', handleDoubleClick);
    return () => canvas.removeEventListener('dblclick', handleDoubleClick);
  }, [gl.domElement, handleDoubleClick]);

  // We need a ref for layerSettings so the dirty-apply path always sees the latest values
  const layerSettingsRef = useRef(layerSettings);
  layerSettingsRef.current = layerSettings;
  const layersRef = useRef(layers);
  layersRef.current = layers;

  // Re-apply color/wireframe to a single model by walking its material set.
  // Used by the settings-change effect AND triggered by new-tile events.
  const applyModelMaterials = useCallback((modelId: string) => {
    if (!fragmentsInstance) return;
    const model = fragmentsInstance.models.list.get(modelId);
    if (!model) return;
    const layerId = modelToLayerRef.current.get(modelId);
    if (!layerId) return;
    const settings = layerSettingsRef.current[layerId];
    if (!settings) return;

    const fallbackColor = new THREE.Color(settings.modelDefaultColor);
    const _white = new THREE.Color(1, 1, 1);

    model.object.traverse((child) => {
      if (!('material' in child)) return;
      const obj = child as THREE.Mesh | THREE.Line | THREE.Points;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of mats) {
        if (!mat) continue;
        if ('wireframe' in mat) (mat as any).wireframe = settings.modelIsWireframe;

        if ('isLodMaterial' in mat && mat.isLodMaterial) {
          const lod = mat as any;
          if (lod.lodColor && lod.lodColor.equals(_white)) {
            lod.lodColor = fallbackColor;
          }
        } else if ('color' in mat) {
          const c = (mat as any).color as THREE.Color;
          if (c.equals(_white)) c.copy(fallbackColor);
        }
        mat.needsUpdate = true;
      }
    });
  }, []);

  // Apply once whenever the user changes color/wireframe in settings.
  // Wireframe-toggle also triggers a full reload via layersKey, but the
  // color path runs without a reload — so this effect handles both.
  const colorKey = useMemo(
    () =>
      layers
        .map((l) => `${l.id}:${layerSettings[l.id]?.modelDefaultColor}:${layerSettings[l.id]?.modelIsWireframe}`)
        .join(','),
    [layers, layerSettings],
  );
  useEffect(() => {
    if (!fragmentsInstance) return;
    for (const layer of layers) applyModelMaterials(`frag-${layer.id}`);
  }, [colorKey, layers, applyModelMaterials]);

  // Newly streamed LOD tiles bring in fresh materials — apply current
  // settings to them as they arrive instead of re-traversing every frame.
  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => void) | null = null;
    (async () => {
      const fragments = await getFragments();
      if (cancelled) return;
      const handler = () => {
        // We don't get model context from the global event, so just refresh
        // every loaded layer once. This is still a single pass per new tile,
        // not per frame.
        for (const layer of layersRef.current) {
          applyModelMaterials(`frag-${layer.id}`);
        }
      };
      fragments.models.materials.list.onItemSet.add(handler);
      cleanup = () => fragments.models.materials.list.onItemSet.remove(handler);
    })();
    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [applyModelMaterials]);

  // Per-frame: only the things that genuinely need to run every frame —
  // ThatOpen's streaming update + clip-plane application.
  useFrame(() => {
    if (!fragmentsInstance) return;
    fragmentsInstance.update();

    // --- Clip box → Three.js clipping planes ---
    const { enabled, clipMode, center, halfSize, rotation: rot } = clipBoxState;
    const shouldClip = enabled && center !== null && (clipMode === 'CLIP_OUTSIDE' || clipMode === 'CLIP_INSIDE');

    if (!shouldClip) {
      // Remove clipping planes from all fragment materials
      if (clipPlanesRef.current.length > 0) {
        clipPlanesRef.current = [];
        groupRef.current?.traverse((child) => {
          if (!('material' in child)) return;
          const obj = child as THREE.Mesh;
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          for (const mat of mats) {
            if (mat) {
              mat.clippingPlanes = null;
              mat.needsUpdate = true;
            }
          }
        });
      }
      return;
    }

    // Build 6 clipping planes from the oriented box.
    // Planes are oriented so the positive half-space = inside the box.
    const q = new THREE.Quaternion(rot[0], rot[1], rot[2], rot[3]);
    const c = new THREE.Vector3(center![0], center![1], center![2]);
    const hs: [number, number, number] = [halfSize[0], halfSize[1], halfSize[2]];

    // Axis unit vectors rotated by the box quaternion
    const axes = [
      new THREE.Vector3(1, 0, 0).applyQuaternion(q),
      new THREE.Vector3(0, 1, 0).applyQuaternion(q),
      new THREE.Vector3(0, 0, 1).applyQuaternion(q),
    ];

    // Ensure we have exactly 6 planes
    while (clipPlanesRef.current.length < 6) clipPlanesRef.current.push(new THREE.Plane());
    clipPlanesRef.current.length = 6;

    for (let i = 0; i < 3; i++) {
      const d = c.dot(axes[i]); // projection of center onto this axis
      // Positive face: normal points inward (−axis), keeps points where dot(p, axis) <= d + h
      // Three.js keeps dot(p, normal) + constant >= 0
      // → normal = -axis, constant = d + h
      clipPlanesRef.current[i * 2].set(axes[i].clone().negate(), d + hs[i]);
      // Negative face: normal points inward (+axis), keeps points where dot(p, axis) >= d - h
      // → normal = +axis, constant = -(d - h) = -d + h
      clipPlanesRef.current[i * 2 + 1].set(axes[i].clone(), -d + hs[i]);
    }

    // CLIP_OUTSIDE: keep inside box → clipIntersection = false
    //   (clips pixels on negative side of ANY plane = outside the box)
    // CLIP_INSIDE: keep outside box → flip all planes + clipIntersection = true
    //   (clips pixels on negative side of ALL flipped planes = inside the box)
    const isClipInside = clipMode === 'CLIP_INSIDE';
    if (isClipInside) {
      for (const plane of clipPlanesRef.current) {
        plane.negate();
      }
    }

    // Apply to all fragment mesh materials
    gl.localClippingEnabled = true;
    groupRef.current?.traverse((child) => {
      if (!('material' in child)) return;
      const obj = child as THREE.Mesh;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of mats) {
        if (mat) {
          mat.clippingPlanes = clipPlanesRef.current;
          mat.clipIntersection = isClipInside;
        }
      }
    });
  });

  useEffect(() => {
    const abortController = new AbortController();
    const modelIds: string[] = [];

    const loadAll = async () => {
      const fragments = await getFragments();

      for (const layer of layers) {
        if (abortController.signal.aborted) return;
        if (!layer.root) continue;

        const modelId = `frag-${layer.id}`;
        modelIds.push(modelId);
        modelToLayerRef.current.set(modelId, layer.id);

        if (fragments.models.list.has(modelId)) continue;

        try {
          const url = `${import.meta.env.VITE_RESOURCES_ENDPOINT}/${layer.root}`;
          const response = await fetch(url, {
            signal: abortController.signal,
            credentials: 'include',
          });
          const buffer = await response.arrayBuffer();

          if (abortController.signal.aborted) return;

          const model = await fragments.load(buffer, { modelId });
          model.useCamera(camera);

          // coords = [easting, height, northing, ...] from IfcMapConversion
          const coords = await model.getCoordinates();
          const worldPos =
            coords.length >= 3 ? new THREE.Vector3(-coords[0], coords[2], -coords[1]) : new THREE.Vector3();
          modelWorldPosRef.current.set(modelId, worldPos);

          if (groupRef.current) {
            // Each model gets its own absolutely-positioned sub-group
            const modelGroup = new THREE.Group();
            modelGroup.position.copy(worldPos);
            modelGroup.rotation.set(Math.PI / 2, 0, 0);
            modelGroup.add(model.object);
            groupRef.current.add(modelGroup);
            modelGroups.set(modelId, modelGroup);
          }

          fragments.update(true);
          setLoadedIds((prev) => new Set([...prev, modelId]));
        } catch (err) {
          if (!abortController.signal.aborted) {
            console.error(`[IFC2] Failed to load fragment for layer ${layer.id}:`, err);
          }
        }
      }
    };

    loadAll();

    return () => {
      abortController.abort();
      // Clean up highlight mesh
      clearHighlight();
      if (groupRef.current) {
        while (groupRef.current.children.length) {
          groupRef.current.remove(groupRef.current.children[0]);
        }
      }
      (async () => {
        const fragments = await getFragments();
        for (const id of modelIds) {
          try {
            await fragments.disposeModel(id);
          } catch {
            // model may not have been loaded yet
          }
        }
        setLoadedIds(new Set());
        modelToLayerRef.current.clear();
        modelWorldPosRef.current.clear();
        for (const id of modelIds) modelGroups.delete(id);
      })();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersKey, camera]);

  // HIGHLIGHT_INSIDE: compute box matrix for the orange overlay mesh
  const highlightBoxMatrix = useMemo(() => {
    const { enabled, clipMode, center, halfSize, rotation: rot } = clipBoxState;
    if (!enabled || clipMode !== 'HIGHLIGHT_INSIDE' || !center) return null;
    const m = new THREE.Matrix4();
    m.compose(
      new THREE.Vector3(center[0], center[1], center[2]),
      new THREE.Quaternion(rot[0], rot[1], rot[2], rot[3]),
      new THREE.Vector3(halfSize[0] * 2, halfSize[1] * 2, halfSize[2] * 2),
    );
    return m;
  }, [clipBoxState]);

  return (
    <>
      <group ref={groupRef} />
      {highlightBoxMatrix && (
        <mesh matrixAutoUpdate={false} matrix={highlightBoxMatrix} renderOrder={996}>
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial color='#ff8c00' transparent opacity={0.08} side={THREE.DoubleSide} depthTest={false} />
        </mesh>
      )}
    </>
  );
};
